import { ComponentProps, MouseEvent } from 'react';
import { MdCheck, MdContentCopy } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { useCopyToClipboard } from '@src/utils/hooks/useCopyToClipboard';

type ButtonProps = ComponentProps<typeof Button>;

interface CopyButtonProps extends Omit<
  ButtonProps,
  | 'onClick'
  | 'children'
  | 'tooltip'
  | 'leadingElement'
  | 'className'
  | 'appearance'
  | 'design'
  | 'size'
> {
  text: string;
  // Labeled button ('Copied' while copied) instead of an icon-only one with a tooltip.
  label?: string;
  // Icon-only tooltip while idle; flips to 'Copied!' after a copy.
  tooltipLabel?: string;
  onCopied?: () => void;
  // For a copy button inside a clickable row/card, so the copy doesn't also trigger the parent.
  stopPropagation?: boolean;
}

export const CopyButton = ({
  text,
  label,
  tooltipLabel = 'Copy',
  onCopied,
  stopPropagation,
  ...buttonProps
}: CopyButtonProps) => {
  const { copyToClipboard, copiedText } = useCopyToClipboard();
  const copied = copiedText === text;

  const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    if (await copyToClipboard(text)) {
      onCopied?.();
    }
  };

  const icon = copied ? <MdCheck /> : <MdContentCopy />;

  if (label) {
    return (
      <Button
        {...buttonProps}
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        size={Size.SM}
        leadingElement={icon}
        onClick={handleClick}
      >
        {copied ? 'Copied' : label}
      </Button>
    );
  }
  return (
    <IconButton
      aria-label={tooltipLabel}
      {...buttonProps}
      appearance={Appearance.EMPTY}
      design={Design.NEUTRAL}
      size={Size.SM}
      tooltip={{ text: copied ? 'Copied!' : tooltipLabel }}
      onClick={handleClick}
    >
      {icon}
    </IconButton>
  );
};
